import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { MessageSquare, User, Calendar, Filter, Search, Plus, Eye, Star, RefreshCw } from 'lucide-react';
import { supabase } from '@/lib/supabase';

interface Review {
  id: string;
  company_id: string;
  review_text: string;
  review_ratings: any;
  review_type: string;
  sentiment: string;
  is_approved: boolean;
  reviewer_info: any;
  employee_id: string;
  created_at: string;
  updated_at: string;
  company_name?: string;
}

const ReviewsManagementPage: React.FC = () => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadReviews();
  }, []);

  const loadReviews = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!supabase) throw new Error('Supabase not initialized');

      const { data: reviewsData, error: reviewsError } = await supabase
        .from('reviews')
        .select(`
          *,
          sw_companies!inner(name)
        `)
        .order('created_at', { ascending: false })
        .limit(100);

      if (reviewsError) {
        throw new Error(reviewsError.message);
      }

      const reviewsWithCompany = reviewsData?.map(review => ({
        ...review,
        company_name: review.sw_companies?.name || 'Unknown Company'
      })) || [];

      setReviews(reviewsWithCompany);
    } catch (err: any) {
      setError(err.message);
      console.error('Error loading reviews:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateReviewStatus = async (reviewId: string, isApproved: boolean) => {
    try {
      if (!supabase) throw new Error('Supabase not initialized');

      const { error: updateError } = await supabase
        .from('reviews')
        .update({
          is_approved: isApproved,
          approved_by: 'admin',
          updated_at: new Date().toISOString()
        })
        .eq('id', reviewId);

      if (updateError) {
        throw new Error(updateError.message);
      }

      await loadReviews();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const getSentimentColor = (sentiment: string) => {
    switch (sentiment) {
      case 'positive': return 'text-green-600 bg-green-100';
      case 'negative': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const renderStarRating = (ratings: any) => {
    if (!ratings) return null;

    try {
      const ratingsObj = typeof ratings === 'string' ? JSON.parse(ratings) : ratings;
      const rateValue = ratingsObj?.rate1?.value || ratingsObj?.rating || 0;
      const starCount = Math.round(rateValue / 2);

      return (
        <div className="flex items-center gap-1">
          {[1, 2, 3, 4, 5].map((star) => (
            <Star
              key={star}
              className={`h-4 w-4 ${
                star <= starCount ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
              }`}
            />
          ))}
          <span className="text-sm text-gray-600 ml-1">({rateValue}/10)</span>
        </div>
      );
    } catch {
      return <span className="text-sm text-gray-500">Invalid rating data</span>;
    }
  };

  const filteredReviews = reviews.filter(review =>
    review.review_text?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    review.reviewer_info?.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 text-white">
        <div className="max-w-7xl mx-auto p-6">
          <div className="flex items-center justify-between mb-8">
            <h1 className="text-3xl font-bold text-white">Reviews Management</h1>
          </div>
          <div className="flex items-center justify-center h-64">
            <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <div className="max-w-7xl mx-auto p-6">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-white">Reviews Management</h1>
          <div className="flex gap-3">
            <button className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              <Plus className="h-4 w-4" />
              Add Review
            </button>
            <button
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              onClick={loadReviews}
            >
              <MessageSquare className="h-4 w-4" />
              Refresh
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-6 bg-red-900 border border-red-700 rounded-lg">
            <div className="p-4 text-red-200">Error: {error}</div>
          </div>
        )}

        {/* Search and Filters */}
        <div className="mb-6 bg-slate-800 rounded-lg">
          <div className="p-6">
            <div className="flex items-center gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search reviews..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <button className="flex items-center gap-2 px-4 py-2 border border-slate-600 rounded-lg hover:bg-slate-700 text-white">
                <Filter className="h-4 w-4" />
                Filters
              </button>
            </div>
          </div>
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="bg-slate-800 rounded-lg">
            <div className="p-6">
              <div className="text-3xl font-bold text-white">{reviews.length}</div>
              <div className="text-slate-400">Total Reviews</div>
            </div>
          </div>
          <div className="bg-slate-800 rounded-lg">
            <div className="p-6">
              <div className="text-3xl font-bold text-green-500">
                {reviews.filter(r => r.sentiment === 'positive').length}
              </div>
              <div className="text-slate-400">Positive</div>
            </div>
          </div>
          <div className="bg-slate-800 rounded-lg">
            <div className="p-6">
              <div className="text-3xl font-bold text-red-500">
                {reviews.filter(r => r.sentiment === 'negative').length}
              </div>
              <div className="text-slate-400">Negative</div>
            </div>
          </div>
          <div className="bg-slate-800 rounded-lg">
            <div className="p-6">
              <div className="text-3xl font-bold text-blue-500">
                {reviews.filter(r => r.is_approved).length}
              </div>
              <div className="text-slate-400">Approved</div>
            </div>
          </div>
        </div>

        {/* Reviews List */}
        <div className="space-y-4">
          {filteredReviews.map((review) => (
            <div key={review.id} className="bg-slate-800 rounded-lg">
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <User className="h-4 w-4 text-gray-500" />
                      <span className="font-medium">
                        {review.reviewer_info?.name || 'Anonymous'}
                      </span>
                      <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded">
                        {review.company_name}
                      </span>
                      <span className={`px-2 py-1 text-xs font-medium rounded ${getSentimentColor(review.sentiment)}`}>
                        {review.sentiment}
                      </span>
                      <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                        {review.review_type}
                      </span>
                    </div>
                    {renderStarRating(review.review_ratings)}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-3 py-1 text-sm font-medium rounded ${
                      review.is_approved
                        ? 'bg-green-100 text-green-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {review.is_approved ? 'Approved' : 'Pending'}
                    </span>
                    <div className="flex gap-2">
                      {!review.is_approved && (
                        <button
                          onClick={() => updateReviewStatus(review.id, true)}
                          className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700"
                        >
                          Approve
                        </button>
                      )}
                      {review.is_approved && (
                        <button
                          onClick={() => updateReviewStatus(review.id, false)}
                          className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50"
                        >
                          Unapprove
                        </button>
                      )}
                      <button className="p-1 border border-gray-300 rounded hover:bg-gray-50">
                        <Eye className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>

                <p className="text-slate-300 mb-4">{review.review_text}</p>

                <div className="flex items-center justify-between text-sm text-slate-400 pt-4 border-t border-slate-700">
                  <div className="flex items-center gap-4">
                    <span>Employee: {review.employee_id || 'N/A'}</span>
                    <span>Email: {review.reviewer_info?.email || 'N/A'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    <span>{new Date(review.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {filteredReviews.length === 0 && (
            <div className="bg-slate-800 rounded-lg">
              <div className="p-12 text-center">
                <MessageSquare className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                <p className="text-slate-300 mb-2">No reviews found matching your criteria.</p>
                <p className="text-sm text-slate-400">
                  Try adjusting your search or import review data from the integrations.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReviewsManagementPage;